import type { ComponentProps } from "react";
import { useCallback, useState } from "react";
import type { TickProps } from "@ptsd/uikit";
import {
  Button,
  Card,
  ChartContainer,
  ChartTooltip,
  ChevronDown,
  getChartLines,
  getItemIndex,
  Legend,
  MultiSelect,
  YAxisTick,
} from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack } from "tamagui";

import { api } from "~/utils/api";
import { dateToReadable } from "~/utils/date-to-readable";
import { EmptyStateBase, LoadingScreen } from "~/components/blocks";
import { HowToReadTooltip } from "./how-to-read-tooltip";

interface LinearChartByScaleAndSurveyTypeProps {
  scales: { name: string; value: string }[];
  researchId: string;
}

type CardProps = ComponentProps<typeof Card>;

export const LinearChartByScaleAndSurveyType: React.FC<
  LinearChartByScaleAndSurveyTypeProps & CardProps
> = ({ researchId, scales, ...cardProps }) => {
  const { t } = useTranslation("slices");
  const [selectedScales, setSelectedScales] = useState<string[]>(
    scales.slice(0, 5).map((s) => s.value),
  );

  const { data, isLoading } = api.phase.getScalesStats.useQuery(
    {
      scales: selectedScales,
      researchId,
    },
    {
      select: (data) => {
        const config = Object.fromEntries(
          data.scales.map(({ key, color, name }) => [
            key,
            { label: name, color },
          ]),
        );

        return {
          config,
          chartData: data.chartData,
        };
      },
    },
  );

  const labelDateFormatter = useCallback(
    (value: string) => dateToReadable(value, true),
    [],
  );

  if (isLoading) return <LoadingScreen height={500} width={"100%"} />;

  if (!scales || scales.length === 0) return null;

  return (
    <Card
      data-id={"involvement-dynamics-chart-card"}
      paddingVertical={"$4.1"}
      shadow="md"
      gap={"$5"}
      {...cardProps}
    >
      <XStack alignItems="center" justifyContent="space-between">
        <Card.Title
          textAlign="left"
          data-id={"involvement-dynamics-chart-title"}
        >
          {t("results.linear-chart-by-scale-and-survey-type.title")}
        </Card.Title>
        <XStack gap={"$1"} alignItems="center">
          <MultiSelect
            trigger={
              <Button
                data-id={"involvement-dynamics-chart-scales"}
                variant="gray"
                size="sm"
                rounded="lg"
                iconAfter={<ChevronDown color="#23252A" />}
              >
                {t(
                  "results.linear-chart-by-scale-and-survey-type.multi-select-trigger",
                )}
              </Button>
            }
            maxSelectedCount={5}
            options={scales}
            value={selectedScales}
            onChange={setSelectedScales}
          />

          <HowToReadTooltip />
        </XStack>
      </XStack>

      {selectedScales.length < 1 ? (
        <EmptyStateBase
          text={t("results.linear-chart-by-scale-and-survey-type.empty-state")}
        />
      ) : (
        <View position="relative" gap={"$4"}>
          <ChartContainer
            data-id={"involvement-dynamics-chart-container"}
            height={400}
            config={data?.config ?? {}}
          >
            <LineChart
              data-id={"involvement-dynamics-chart"}
              accessibilityLayer
              data={data?.chartData}
              margin={{
                left: -10,
                right: 20,
                top: 12,
                bottom: 20,
              }}
            >
              <CartesianGrid
                data-id={"involvement-dynamics-chart-grid"}
                strokeDasharray={"3 3"}
                stroke="#CBCBCC"
                vertical={false}
              />
              <XAxis
                data-id={"involvement-dynamics-chart-x-axis"}
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={24}
                tick={{
                  fill: "#636668",
                  fontFamily: "Fixel Text",
                  fontSize: 14,
                  fontWeight: 400,
                }}
                tickFormatter={(v) => {
                  const index = getItemIndex(
                    data?.chartData ?? [],
                    "date",
                    `${v}`,
                  );
                  return "Етап " + (index + 1);
                }}
              />
              <YAxis
                data-id={"involvement-dynamics-chart-y-axis"}
                tickLine={false}
                axisLine={false}
                tickMargin={16}
                tick={(props: TickProps) => <YAxisTick {...props} />}
              />
              <Tooltip
                data-id={"involvement-dynamics-chart-tooltip"}
                cursor={{
                  stroke: "#CBCBCC",
                  strokeWidth: 1,
                  strokeDasharray: "3 3",
                }}
                content={
                  <ChartTooltip.Content
                    data-id={"involvement-dynamics-chart-tooltip-content"}
                  >
                    {(payload, _, date) => {
                      return (
                        <ChartTooltip.Container
                          data-id={
                            "involvement-dynamics-chart-tooltip-container"
                          }
                        >
                          <ChartTooltip.Title
                            data-id={"involvement-dynamics-chart-tooltip-title"}
                          >
                            {labelDateFormatter(date ?? "")}
                          </ChartTooltip.Title>
                          <View
                            gap={"$0.5"}
                            data-id={"involvement-dynamics-chart-tooltip-body"}
                          >
                            {payload?.map(({ color, value, config }, i) => (
                              <XStack
                                data-id={`involvement-dynamics-chart-tooltip-item-${i}`}
                                gap={"$0.5"}
                                alignItems="center"
                                key={i}
                              >
                                <Circle
                                  backgroundColor={"white"}
                                  borderWidth={"$0.3"}
                                  borderColor={color}
                                  size={"$2"}
                                />

                                <Text
                                  data-id={`involvement-dynamics-chart-tooltip-text-${i}`}
                                  fontFamily={"$body"}
                                  fontSize={"$1"}
                                  color={"black"}
                                >
                                  {config?.label} {value}
                                </Text>
                              </XStack>
                            ))}
                          </View>
                        </ChartTooltip.Container>
                      );
                    }}
                  </ChartTooltip.Content>
                }
              />
              {getChartLines(data?.config ?? {}).map(
                ({ dataKey, color }, i) => (
                  <Line
                    data-id={`involvement-dynamics-chart-line`}
                    data-key={dataKey}
                    key={i}
                    dataKey={dataKey}
                    type="linear"
                    stroke={color}
                    strokeWidth={2.5}
                    dot={{
                      r: 8,
                      strokeWidth: 2.5,
                      stroke: color,
                      fill: "white",
                    }}
                    activeDot={{
                      r: 3,
                      strokeWidth: 2.5,
                      stroke: color,
                      fill: color,
                    }}
                  />
                ),
              )}
            </LineChart>
          </ChartContainer>

          <Legend config={data?.config ?? {}} />
        </View>
      )}
    </Card>
  );
};
